<template>
  <TransitionRoot
    :show="modal.isVisible(name)"
    @beforeEnter="emit('onOpen')"
    @beforeLeave="emit('onClose')"
  >
    <Dialog class="z-modal fixed inset-0" @close="closeModal">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-black bg-opacity-30"
          @click="closeModal"
        ></div>
      </TransitionChild>
      <div class="fixed inset-0 overflow-hidden">
        <div
          class="vh-screen flex flex-col items-center justify-center overflow-hidden p-5 md:p-6"
        >
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <slot name="dialog-panel" v-bind="{ closeModal }">
              <DialogPanel
                class="flex w-max max-w-full flex-col items-center justify-center overflow-hidden rounded-2xl bg-white p-5 shadow-2xl md:p-6"
              >
                <div
                  v-if="closable"
                  class="relative flex w-full items-start space-x-4 pb-4"
                  :class="
                    Boolean($slots['dialog-title'])
                      ? 'justify-between'
                      : 'justify-end'
                  "
                >
                  <DialogTitle
                    v-if="Boolean($slots['dialog-title'])"
                    class="flex-1 text-xl font-bold"
                  >
                    <slot name="dialog-title" v-bind="{ closeModal }"></slot>
                  </DialogTitle>
                  <button
                    type="button"
                    class="absolute right-0 outline-none duration-200 hover:scale-105 focus:scale-105 focus:outline-none"
                    @click="closeModal"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      fill="none"
                    >
                      <path
                        fill="#F1F2F7"
                        fill-rule="evenodd"
                        d="M15 30c8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15C6.716 0 0 6.716 0 15c0 8.284 6.716 15 15 15Zm5.635-20.737a1 1 0 0 0-1.414 0L15 13.483l-4.22-4.22a1 1 0 0 0-1.415 0l-.102.102a1 1 0 0 0 0 1.414L13.483 15l-4.22 4.22a1 1 0 0 0 0 1.415l.102.102a1 1 0 0 0 1.414 0L15 16.517l4.22 4.22a1 1 0 0 0 1.415 0l.102-.102a1 1 0 0 0 0-1.414L16.517 15l4.22-4.22a1 1 0 0 0 0-1.415l-.102-.102Z"
                        clip-rule="evenodd"
                      />
                      <path
                        fill="#A4A2B7"
                        fill-rule="evenodd"
                        d="M10.804 9.295a1 1 0 0 0-1.415 0l-.094.094a1 1 0 0 0 0 1.415L13.49 15l-4.196 4.196a1 1 0 0 0 0 1.414l.094.095a1 1 0 0 0 1.415 0L15 16.509l4.196 4.196a1 1 0 0 0 1.414 0l.095-.095a1 1 0 0 0 0-1.414L16.509 15l4.196-4.196a1 1 0 0 0 0-1.415l-.095-.094a1 1 0 0 0-1.414 0L15 13.49l-4.196-4.196Z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
                <div class="grid flex-1 grid-cols-1 overflow-hidden">
                  <slot v-bind="{ closeModal }"></slot>
                </div>
              </DialogPanel>
            </slot>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
  import {
    Dialog,
    DialogTitle,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
  } from '@headlessui/vue';
  import { injectModal } from '../model';

  type Props = {
    name: string;
    closable?: boolean;
  };

  const props = withDefaults(defineProps<Props>(), {
    closable: true,
  });

  const emit = defineEmits<{
    onOpen: [];
    onClose: [];
  }>();

  const modal = injectModal();

  function closeModal() {
    if (props.closable) {
      modal.hide(props.name);
    }
  }
</script>
